"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SeparateTradingProcessor = void 0;
const TradeAPILib_1 = require("TradeAPILib");
const react_1 = require("react");
const trade_utils_lib_1 = require("trade_utils_lib");
const GridTradingUtils_1 = require("../GridTrading/GridTradingUtils");
const SimpleTrading_1 = require("../SimpleTrading");
const TextAndSliderSyncTool_1 = require("../CommonUtils/TextAndSliderSyncTool");
exports.SeparateTradingProcessor = {
    useUserParams(top, bottom, count, perAmount, investPercent, actionType, gridType, side, baseAvailable, quoteAvailable, symbol, orderType, baseTotalBalance, currentPrice, leverageCoinInfo) {
        const minPerAmount = (0, react_1.useMemo)(() => {
            const min = SimpleTrading_1.SimpleTrading.getMinAmount(symbol, new trade_utils_lib_1.SafeDecimal(bottom).toNumber());
            return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(min, symbol.amount);
        }, [symbol, bottom]);
        const available = (0, react_1.useMemo)(() => {
            if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
                return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(quoteAvailable, symbol.precision);
            }
            else {
                return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(baseAvailable, symbol.amount);
            }
        }, [side, symbol, quoteAvailable, baseAvailable]);
        const [prices, sumPrice] = (0, react_1.useMemo)(() => {
            try {
                const priceArr = GridTradingUtils_1.GridTradingUtils.getPriceInterverArr(top, bottom, count, gridType);
                const priceStrings = priceArr.map((e) => {
                    return trade_utils_lib_1.NumberCommonUtils.formatterDecimalToStr(e, symbol.precision);
                });
                const sumPrice = priceArr
                    .reduce((total, current, curIndex, arr) => {
                    return total.add(current);
                })
                    .toString();
                return [priceStrings, sumPrice];
            }
            catch (_a) {
                return [[], ""];
            }
        }, [top, bottom, count, gridType, symbol.precision]);
        const [parseAmount, parsePercent, volume] = TextAndSliderSyncTool_1.TextAndSliderSyncTool.useSeparateTradingSync(actionType, perAmount, investPercent, side, count, sumPrice, available, symbol);
        const investState = (0, react_1.useMemo)(() => {
            const state = {
                state: "normal",
                min: minPerAmount + `(${symbol.baseDisplay})`,
                max: available + `(${side === TradeAPILib_1.ExchangeOrderSide.buy ? symbol.quote : symbol.baseDisplay})`,
            };
            if (parseAmount && volume) {
                if (new trade_utils_lib_1.SafeDecimal(parseAmount).lessThan(new trade_utils_lib_1.SafeDecimal(minPerAmount))) {
                    state.state = "small";
                }
                if (side === TradeAPILib_1.ExchangeOrderSide.buy) {
                    if (new trade_utils_lib_1.SafeDecimal(volume).greaterThan(new trade_utils_lib_1.SafeDecimal(available))) {
                        state.state = "large";
                    }
                }
                else {
                    const totalInvest = trade_utils_lib_1.NumberCommonUtils.formatterDecimal(new trade_utils_lib_1.SafeDecimal(parseAmount).mul(new trade_utils_lib_1.SafeDecimal(count)), symbol.amount);
                    if (totalInvest.greaterThan(available)) {
                        state.state = "large";
                    }
                }
            }
            return state;
        }, [minPerAmount, symbol.baseDisplay, symbol.quote, symbol.amount, available, side, parseAmount, volume, count]);
        const emptyErrors = (0, react_1.useMemo)(() => {
            const emptys = GridTradingUtils_1.GridTradingUtils.checkRequiredParamsEmpty(top, bottom, count);
            if (!Number(parseAmount)) {
                emptys.push("trade_grid_per_volume");
            }
            return emptys;
        }, [top, bottom, count, parseAmount]);
        return {
            orderType,
            side,
            top,
            bottom,
            count,
            gridType,
            perAmount: parseAmount,
            investPercent: parsePercent,
            currentPrice,
            available,
            symbol,
            investState,
            minInvest: minPerAmount,
            volume,
            limitPrices: prices,
            emptyErrors,
            baseTotalBalance,
            leverageCoinInfo,
        };
    },
    getCreateOrderRequest(params, $st) {
        if (params.emptyErrors.length > 0) {
            throw $st("trade_params_not_fill_enough");
        }
        if (new trade_utils_lib_1.SafeDecimal(params.bottom).greaterThanOrEqualTo(new trade_utils_lib_1.SafeDecimal(params.top))) {
            throw $st("trade_bottom_need_lower_than_high");
        }
        if (params.limitPrices.length < 2 || params.limitPrices.length > 10) {
            throw $st("separate_row_wrong", { min: 2, max: 10 });
        }
        if (params.investState.state === "small") {
            throw $st("trade_error_low_amount", {
                amount: `${params.minInvest} ${params.symbol.baseDisplay}`,
            });
        }
        else if (params.investState.state === "large") {
            throw $st("trade_error_balance_not_enough");
        }
        const symbol = params.symbol;
        let tradeCost = new trade_utils_lib_1.SafeDecimal(0);
        const requests = params.limitPrices.map((price) => {
            // 价格数值是否可用
            const priceDecimal = SimpleTrading_1.SimpleTrading.parsePriceSupportTrading(price, symbol, $st);
            // 交易量是否可用
            const quantityDecimal = SimpleTrading_1.SimpleTrading.parseAmountSupportTrading(params.perAmount, params.symbol, priceDecimal.toString(), $st);
            const request = {
                base: symbol.base,
                quote: symbol.quote,
                side: params.side,
                type: params.orderType,
                price: priceDecimal.toString(),
                amount: quantityDecimal.toString(),
            };
            tradeCost = tradeCost.add(priceDecimal.mul(quantityDecimal));
            return request;
        });
        if (params.side === TradeAPILib_1.ExchangeOrderSide.buy && params.leverageCoinInfo) {
            const currentLeveragePosition = new trade_utils_lib_1.SafeDecimal(params.baseTotalBalance).mul(params.currentPrice).add(tradeCost);
            if (tradeCost.greaterThan(params.leverageCoinInfo.quota)) {
                throw $st("trade_cost_than_leverage_amount", { amount: params.leverageCoinInfo.quota });
            }
            else if (currentLeveragePosition.greaterThan(params.leverageCoinInfo.accountQuota)) {
                throw $st("trade_cost_than_leverage_pair_limit", { amount: params.leverageCoinInfo.accountQuota });
            }
        }
        return requests;
    },
};
